'use client';

import { c as _c } from "react/compiler-runtime";
import type React from 'react';
import { useEffect } from 'react';
import { shallow } from 'zustand/shallow';
import { USER_ANALYSIS_TWEET_LIMIT } from '@/constants/user-analysis';
import type { TimeRange } from '@/types/timeline-analysis';
import { type TimelineContextValue, useTimelineStore } from './timeline-store';
export type { TimelineListFilter, TimelineTopicFilter } from './timeline-store';
type TimelineContextSnapshot = TimelineContextValue & {
  timeRange: TimeRange;
};
export function TimelineProvider(t0) {
  const $ = _c(3);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  useEffect(_temp2, t1);
  let t2;
  if ($[1] !== children) {
    t2 = <>{children}</>;
    $[1] = children;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function _temp2() {
  const store = useTimelineStore.getState();
  store.reset();
  store.markActive(true);
  return _temp;
}
function _temp() {
  const latest = useTimelineStore.getState();
  latest.markActive(false);
  latest.reset();
}
export function useTimelineContext() {
  const isActive = useTimelineStore(_temp3);
  const snapshot = useTimelineStore(_temp4, shallow);
  if (!isActive) {
    return null;
  }
  return snapshot;
}
function _temp4(state_0) {
  return {
    timeRange: state_0.timeRange,
    setTimeRange: state_0.setTimeRange,
    isRefreshing: state_0.isRefreshing,
    setIsRefreshing: state_0.setIsRefreshing,
    handleRefresh: state_0.handleRefresh,
    setHandleRefresh: state_0.setHandleRefresh,
    handleAnalysisRequest: state_0.handleAnalysisRequest,
    setHandleAnalysisRequest: state_0.setHandleAnalysisRequest,
    registerAnalysisHandler: state_0.registerAnalysisHandler,
    topicFilter: state_0.topicFilter,
    setTopicFilter: state_0.setTopicFilter,
    clearTopicFilter: state_0.clearTopicFilter,
    listFilter: state_0.listFilter,
    setListFilter: state_0.setListFilter,
    clearListFilter: state_0.clearListFilter,
    lists: state_0.lists,
    setLists: state_0.setLists,
    topics: state_0.topics,
    setTopics: state_0.setTopics,
    timelineTotals: state_0.timelineTotals,
    setTimelineTotals: state_0.setTimelineTotals
  };
}
function _temp3(state) {
  return state.isActive;
}
export function useTimelineAnalysisHandler(handler) {
  const $ = _c(3);
  let t0;
  let t1;
  if ($[0] !== handler) {
    t0 = () => useTimelineStore.getState().registerAnalysisHandler(handler);
    t1 = [handler];
    $[0] = handler;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
}
export function buildAnalysisOptions(options: {
  model?: string | undefined;
  fromTimestamp?: string | undefined;
  maxTweets?: number | undefined;
  preferenceSignals?: boolean | undefined;
  fetchExternalUrls?: boolean | undefined;
  maxLinkSummaries?: number | undefined;
}) {
  const result: {
    model?: string;
    fromTimestamp?: string;
    maxTweets?: number;
    preferenceSignals?: boolean;
    fetchExternalUrls?: boolean;
    maxLinkSummaries?: number;
  } = {};
  if (options.model) {
    result.model = options.model;
  }
  if (options.fromTimestamp) {
    result.fromTimestamp = options.fromTimestamp;
  }
  if (typeof options.maxTweets === 'number' && Number.isFinite(options.maxTweets)) {
    const normalized = Math.max(1, Math.min(USER_ANALYSIS_TWEET_LIMIT, Math.floor(options.maxTweets)));
    result.maxTweets = normalized;
  }
  if (options.preferenceSignals) {
    result.preferenceSignals = true;
  }
  if (options.fetchExternalUrls) {
    result.fetchExternalUrls = true;
  }
  if (typeof options.maxLinkSummaries === 'number' && Number.isFinite(options.maxLinkSummaries)) {
    const normalized = Math.max(0, Math.min(50, Math.floor(options.maxLinkSummaries)));
    if (normalized > 0) {
      result.maxLinkSummaries = normalized;
      if (!result.fetchExternalUrls) {
        result.fetchExternalUrls = true;
      }
    }
  }
  if (Object.keys(result).length === 0) {
    return;
  }
  return result;
}
export { TIMELINE_DEFAULT_TIME_RANGE } from './timeline-store';